import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Spinner } from "@chakra-ui/react";
import { Button } from "newui/components/Button";
import { useRegisterMutation } from "./hooks/useRegisterMutation";
import type { RegistrationPrefill, UserListItem } from "../types/userInvite";
import PasswordEntry from "../components/PasswordEntry";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { FormikPhoneInput } from "newui/components/ui/InputWithLabel";
import { AxiosError } from "axios";
import { getErrorMessage } from "api-v2";

interface CompleteRegistrationStepProps {
  prefill: RegistrationPrefill;
  registrationToken: string;
  /**
   * Called after the register API succeeds. The third arg carries the
   * email + password the user just entered so the parent can perform an
   * auto-login without prompting for credentials a second time. The
   * password is held in component state only and is dropped the moment
   * the auto-login resolves.
   */
  onSuccess: (
    user: UserListItem,
    accessToken: string,
    credentials?: { email: string; password: string }
  ) => void;
}

interface RegisterFormValues {
  password: string;
  password_confirmation: string;
  phone: string;
}

const REGISTRATION_INSTRUCTIONS = ["Enter your phone number (office or mobile)", "Create a password to login with", "If any of your personal details are wrong, you can update these once you have logged in"]

// Password rules now live inside <PasswordEntry/> as field-level
// validators. Only fields PasswordEntry doesn't own need to be declared
// here — currently just the phone format check.
const validationSchema = Yup.object({
  phone: Yup.string()
    .required("Phone number is required")
    .matches(/^\+\d{7,15}$/, "Enter a valid phone number"),
});

export const CompleteRegistrationStep: React.FC<CompleteRegistrationStepProps> = ({
  registrationToken,
  prefill,
  onSuccess,
}) => {
  const register = useRegisterMutation();
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  const handleSubmit = (values: RegisterFormValues) => {
    register.mutate(
      {
        registration_token: registrationToken,
        phone: values.phone,
        password: values.password,
        password_confirmation: values.password_confirmation,
      },
      {
        onSuccess: (data) => {
          // Forward the credentials so the parent can auto-login —
          // making the user re-type their password seconds after they
          // chose it would be hostile UX. Email comes from the prefill
          // (the verified invite email), not user input.
          onSuccess(data.user, data.access_token, {
            email: prefill.email,
            password: values.password,
          });
        },
        onError: (error: AxiosError) => {

          if (!error.response) {
            return setRegistrationError(error.message);
          }

          let errorMessage = "Failed to get error details.";

          try {
            errorMessage = getErrorMessage(error);
          } finally {
            setRegistrationError(errorMessage);
          }
        }
      }
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Complete your registration</h2>
      </div>
      <InstructionsInfoBox title="To complete your account registration..." collapsible instructions={REGISTRATION_INSTRUCTIONS} />

      {/* Read-only pre-filled fields */}
      <div className="space-y-3">
        <div className="grid grid-cols-[85px_1fr] items-center text-sm font-medium gap-2">
          <div className="text-gray-700">
            Name
          </div>

          <div>
            {prefill.first_name} {prefill.last_name}
          </div>
          <div className="text-gray-700">
            Email
          </div>

          <div>
            {prefill.email}
          </div>
        </div>
      </div>

      <Formik
        initialValues={{ phone: "+61", password: "", password_confirmation: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form className="space-y-4" data-testid="registration-form">
          <FormikPhoneInput
            name="phone"
            label="Phone number"
            required
          />

          <PasswordEntry passwordConfirmationDataTestid="registration-password-confirm" passwordDataTestid="registration-password" showConfirmPassword />

          {register.isError && (
            <p className="text-sm text-red-600" role="alert">
              {registrationError ?? "Registration failed. Please try again."}
            </p>
          )}

          <Button
            type="submit"
            disabled={register.isPending}
            className="w-full"
            data-testid="registration-submit"
          >
            {register.isPending ? <Spinner size="sm" /> : "Create account"}
          </Button>
        </Form>
      </Formik>
    </div>
  );
};
